import * as React from "react";
import {cs} from "../../../../../../../react/chain-services";
import "./step-3-narrowing-search.scss";
import {Header} from "../../common/header/header";
import {cx} from "emotion";
import {SearchForm} from "./search-form/search-form";
import {UseState} from "../../../../../../../react/use-state";
import {Radio} from "../../../../../common/design2/radio/radio";
import {InfoBlock} from "../../common/info-block/info-block";
import {MailOptions} from "./mail-options/mail-options";
import {SectionGroup} from "../../common/section-group";
import {ErrorHighlighter} from "../../../../../../light/error-highlighter";
import {scopedState} from "../../../../../../../react/scoped-state";
import {keyed} from "../../../../../../../react/keyed";

export const Step3NarrowingSearch = ({onReselectTarget, initState, onBack, onSearch, onNext, query}) => cs(
    ["state", (_, next) => UseState({
        initValue: initState ?? {
            mailOptions: {
                mailOption: "all",
            },
        },
        next,
    })],
    ["mailOptions", ({state}, next) => MailOptions({
        state: scopedState(state, ["mailOptions"]),
        total: state.value?.searchResult?.total,
        query,
        next,
    })],
    ["searchForm", ({}, next) => SearchForm({next, query})],

    ["searchFormEH", (_, next) => ErrorHighlighter({next})],
    ["searchTypesEH", (_, next) => ErrorHighlighter({next})],

    ["sectionGroup", ({state, searchForm, searchFormEH, searchTypesEH, mailOptions}, next) => SectionGroup({
        sections: [
            {
                invalid: !searchForm.currentType.value,
                highlightErrors: () => searchTypesEH.start(),
            },
            {
                invalid: !state.value?.searchResult,
                highlightErrors: () => searchFormEH.start(),
            },
            mailOptions,
        ],
        next,
    })],
    ({searchForm, mailOptions, state, searchFormEH, searchTypesEH, sectionGroup}) => (
        <div className="step-3-narrowing-search-9qh">
            {Header({
                icon: require("../../../../mailing-icons/listtype-demographics.png"),
                title: "Demographics Search",
                subTitle: "Create a list around demographic filters.",
            })}

            <div className="content">
                {PrevStep({
                    title: "Select Target",
                    onClick: onReselectTarget,
                })}
                {PrevStep({
                    title: "Customize your Search",
                    onClick: onBack,
                })}

                <div className="form-section">
                    <div className="header">
                        <div className="title" xd-qa-type="label" xd-qa-name="Demographics Search Narrow Target to Desired Area">
                            Narrow Target to Desired Area
                        </div>
                    </div>

                    {Warning({})}

                    {SearchTypes({
                        selected: searchForm.currentType.value || query.value?.searchType,
                        onSelect: searchForm.currentType.onChange,
                        className: searchTypesEH.css,
                    })}


                    {searchForm.currentType.value && cs(
                        ({}, next) => (
                            <div className={cx("search-form", searchFormEH.css)}>
                                {next()}
                            </div>
                        ),
                        keyed(searchForm.currentType.value),
                        () => searchForm.render({
                            showErrors: sectionGroup.showErrors,
                            onSearch: async (geography) => {
                                state.change((_) => ({
                                    ..._,
                                    searchResult: null,
                                }));
                               
                                query.change(() => ({...geography, searchType: searchForm.currentType.value}))
                                const result = await onSearch({searchType: searchForm.currentType.value, geography});

                                if (result.result === "failure") {
                                    throw result.errors?.msg;
                                } else if (!result.total) {
                                    throw "Cannot find any mailing addresses";
                                } else {
                                    const mailOptions = searchForm.currentType.value === "address_quantity" ? ({
                                        mailOption: "all",
                                        useType: state.value.mailOptions.useType,
                                    }) : ({
                                        ...state.value.mailOptions,
                                    });
                                    state.change((_) => ({
                                        ..._,
                                        searchResult: result,
                                        mailOptions,
                                    }));
                                }
                            },
                            query
                        }),
                    )}

                    {state.value?.searchResult && (
                        mailOptions.render({
                            pricePerPiece: state.value.searchResult.pricePerPiece,
                            showErrors: sectionGroup.showErrors,
                            showCountOption: query.value?.searchType !== "address_quantity",
                        })
                    )}

                </div>
            </div>

            <div className="footer">
                <button {...{
                    className: "cancel",
                    onClick: onBack,
                    "xd-qa-type": "button",
                    "xd-qa-name": "Demographics Search Back",
                }}>
                    Back
                </button>

                <button {...{
                    className: cx("next", {disabled: sectionGroup.invalid}),
                    disabled: sectionGroup.submitDisabled,
                    onClick: () => sectionGroup.submit(() => {
                        onNext(state.value);
                    }),
                    "xd-qa-type": "button",
                    "xd-qa-name": "Demographics Search Next",
                }}>
                    Next
                </button>
            </div>

        </div>
    ),
);

const PrevStep = ({title, onClick}) => (
    <div className="prev-section">

        <div className="title" xd-qa-type="label" xd-qa-name={"Demographics Search "+title}>
            {title}
        </div>
        <a className="edit" onClick={onClick} xd-qa-type="link" xd-qa-name={"Demographics Search "+title+" Edit"}>
            Edit
        </a>
    </div>
);

const SearchTypes = ({selected, onSelect, className}) => (
    <div className={cx("search-types", className)}>
        {[
            {
                searchType: "address_distance",
                label: "Distance around an address",
            },
            {
                searchType: "address_quantity",
                label: "Quantity around an address",
            },
            {
                searchType: "postal_code",
                label: "Zip Code",
            },
        ]
            .map(({searchType, label}) => (
                <div
                    className="search-type" key={searchType}
                    onClick={() => onSelect(searchType)}
                >
                    {Radio({
                        selected: searchType === selected,
                        xdQaName: "Demographics Search "+label,
                    })}
                    <div className="label" xd-qa-type="label" xd-qa-name={"Demographics Search "+label}>
                        {label}
                    </div>
                </div>
            ))
        }
    </div>
);

const Warning = ({}) => cs(
    ["closed", (_, next) => UseState({next})],
    ({closed}) => !closed.value && (
        InfoBlock({
            type: "warning",
            content: <>
                <b>Warning!</b> There is a minimum requirement of 100 addresses for demographic search. If you need fewer than 100 addresses, please use our Radius or Street search options.
            </>,
            onClose: () => closed.onChange(true),
            xdQaName: "Demographics Search Minimum Requirement Info"
        })
    )
);
